import React from 'react'
import classNames from 'classnames'
import NavigationItemMenu from 'transfix-ui/components/NavigationItemMenu'
import NotificationBadge from 'transfix-ui/components/NotificationBadge'
import { makeStyles } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { brandColors } from 'transfix-ui/theme/colors'
import { INavRoute } from 'types/routes'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useHover } from '../hooks'
import SubRoutesMenu from './SubRoutesMenu'

const NAVBAR_HEIGHT_PX = 48

const useNavigationItemLinkStyles = makeStyles(theme => ({
  root: {
    display: 'none',
    position: 'relative',
    height: NAVBAR_HEIGHT_PX,
    backgroundColor: brandColors.coolGray8,
    color: brandColors.coolGray4,
    alignItems: 'center',

    /**
     * On desktop, show this item
     */
    [theme.breakpoints.up('lg')]: {
      display: 'inline-flex',
    },
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    color: brandColors.coolGray4,

    '&:hover': {
      color: brandColors.white,
      textDecoration: 'none',
    },

    '&:after': {
      position: 'absolute',
      backgroundColor: brandColors.coolGray8,
      height: 2,
      width: '100%',
      bottom: 0,
      left: 0,
      right: 0,
      content: '""',
    },
  },
  linkHover: {
    cursor: 'pointer',
    color: brandColors.white,
    textDecoration: 'none',

    '&:after': {
      backgroundColor: brandColors.white,
    },
  },
  linkActive: {
    color: brandColors.white,

    '&:after': {
      position: 'absolute',
      backgroundColor: brandColors.white,
      height: 2,
      width: '100%',
      bottom: 0,
      left: 0,
      right: 0,
      content: '""',
    },
  },
  text: {
    fontWeight: 600,
  },
}))

interface INavigationItemLinkProps {
  route: INavRoute
}

const NavigationItemLink = ({ route }: INavigationItemLinkProps) => {
  const {
    childRoutes = [],
    isActive,
    location,
    Menu = NavigationItemMenu,
    MenuProps,
    name,
    notificationCount,
  } = route || {}
  const { isMobile } = useBreakpointContext()
  const { isHover, onMouseEnter, onMouseLeave } = useHover()

  const classes = useNavigationItemLinkStyles()
  const linkClassName = classNames(classes.link, {
    [classes.linkActive]: !!isActive,
    [classes.linkHover]: !!isHover,
  })

  return (
    <li
      data-test='NavigationItemLink'
      className={classes.root}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      <a href={location} className={linkClassName}>
        <NotificationBadge notificationCount={notificationCount} variant='dot'>
          <Typography variant='body2' className={classes.text} component='div'>
            {name}
          </Typography>
        </NotificationBadge>
      </a>
      {MenuProps && <Menu {...MenuProps} isOpen={isHover} isMobile={isMobile} />}
      {childRoutes && <SubRoutesMenu routes={childRoutes} isHover={isHover} />}
    </li>
  )
}

export default NavigationItemLink
